"use client";

import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, Film, ImageIcon, Loader2, Trash2, Youtube } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import Button from "@/components/ui/button/Button";
import {
  useCampaignMedia,
  useUploadCampaignMediaImage,
  useUploadCampaignMediaVideo,
  useAddCampaignMediaEmbed,
  useDeleteCampaignMedia,
  useReorderCampaignMedia,
} from "@/lib/productCatalog";
import { toast } from "@/ui/Toast";
import { userMessage } from "@/api/errors";
import type { CampaignMediaItem } from "@/types/ecommerce";
import { resolveMediaUrl } from '@/utils/mediaUrl';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string | null;
  campaignTitle?: string;
}

function typeIcon(type: CampaignMediaItem["type"]) {
  if (type === "Image") return <ImageIcon className="h-4 w-4" />;
  if (type === "Video") return <Film className="h-4 w-4" />;
  return <Youtube className="h-4 w-4" />;
}

/**
 * A campaign's real multi-item media gallery — images/videos (real uploads via
 * ProductMediaStorage, same as LocationPricingModal's precedent for a bespoke non-EditModal
 * component) plus pasted YouTube/Vimeo links. Reorder is up/down buttons, not drag-and-drop —
 * no drag library exists anywhere in this codebase, and adding one for a handful of gallery
 * items isn't worth the new dependency.
 */
export default function CampaignMediaModal({ isOpen, onClose, campaignId, campaignTitle }: Props) {
  const { data: media = [], isLoading } = useCampaignMedia(campaignId);
  const uploadImage = useUploadCampaignMediaImage(campaignId ?? "");
  const uploadVideo = useUploadCampaignMediaVideo(campaignId ?? "");
  const addEmbed = useAddCampaignMediaEmbed(campaignId ?? "");
  const remove = useDeleteCampaignMedia(campaignId ?? "");
  const reorder = useReorderCampaignMedia(campaignId ?? "");

  const [embedUrl, setEmbedUrl] = useState("");
  const [embedType, setEmbedType] = useState<"YouTube" | "Vimeo">("YouTube");
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const sorted = [...media].sort((a, b) => a.sortOrder - b.sortOrder);

  const handleUpload = async (file: File | undefined, kind: "image" | "video") => {
    if (!file) return;
    try {
      if (kind === "image") await uploadImage.mutateAsync(file);
      else await uploadVideo.mutateAsync(file);
      toast.success("Media added");
    } catch (err) {
      toast.error(userMessage(err));
    }
  };

  const handleAddEmbed = async () => {
    if (!embedUrl.trim()) return;
    try {
      await addEmbed.mutateAsync({ type: embedType, url: embedUrl.trim() });
      setEmbedUrl("");
      toast.success("Media added");
    } catch (err) {
      toast.error(userMessage(err));
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= sorted.length) return;
    const ids = sorted.map((m) => m.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      await reorder.mutateAsync(ids);
    } catch (err) {
      toast.error(userMessage(err));
    }
  };

  const uploading = uploadImage.isPending || uploadVideo.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <h4 className="mb-1 text-xl font-semibold text-gray-800 dark:text-white/90">Media Gallery</h4>
        {campaignTitle && <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{campaignTitle}</p>}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0], "image")}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0], "video")}
          />
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => imageInputRef.current?.click()}>
            {uploadImage.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Add image
          </Button>
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => videoInputRef.current?.click()}>
            {uploadVideo.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />} Add video
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={embedType}
            onChange={(e) => setEmbedType(e.target.value as "YouTube" | "Vimeo")}
            className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="YouTube">YouTube</option>
            <option value="Vimeo">Vimeo</option>
          </select>
          <input
            type="text"
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
            placeholder="Paste a YouTube or Vimeo link"
            className="h-9 flex-1 min-w-[200px] rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <Button size="sm" disabled={addEmbed.isPending || !embedUrl.trim()} onClick={handleAddEmbed}>
            {addEmbed.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Youtube className="h-4 w-4" />} Add link
          </Button>
        </div>

        <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-gray-500">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">No media yet — add an image, video, or a YouTube/Vimeo link above.</p>
          ) : (
            sorted.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 p-2 dark:border-gray-700"
              >
                {item.type === "Image" ? (
                  <img src={resolveMediaUrl(item.url)} alt="" className="h-12 w-16 rounded object-cover bg-gray-100 dark:bg-gray-800" />
                ) : (
                  <div className="flex h-12 w-16 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">
                    {typeIcon(item.type)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    {typeIcon(item.type)} {item.type}
                  </div>
                  <div className="truncate text-xs text-gray-400" title={item.url}>{item.url}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || reorder.isPending}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                    title="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === sorted.length - 1 || reorder.isPending}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                    title="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await remove.mutateAsync(item.id);
                      } catch (err) {
                        toast.error(userMessage(err));
                      }
                    }}
                    className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
